import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Circle,
  Heading,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { deliverables } from './deliverables';
import {
  EventForRenderFragmentGQLType,
  useGetEventsLazyQueryGQLType,
} from './events.gql.generated';
import { fundingRequests } from './fundingRequests';
import { ingestion } from './ingestion';
import { ActionConfig } from './types';
import { verification } from './verification';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { FilterDatePicker } from '~/packages/common-frontend/components/FilterDatePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  PaginationControl,
  usePaginationLimit,
} from '~/packages/common-frontend/components/PaginationControl';

// Constants
const eventConfig: Record<string, Record<string, ActionConfig>> = {
  deliverables,
  fundingRequests,
  ingestion,
  verification,
};

// Fragments
const DefaultEvent = ({ event }: { event: EventForRenderFragmentGQLType }) => {
  const { actorName, created_at: createdAt, domainKey } = event;
  return (
    <Box
      display="flex"
      alignContent="center"
      borderBottom="1px"
      borderColor="gray.100"
      marginTop="10px"
      marginBottom="10px"
    >
      <Circle size="40px">
        <Icon icon="edit" />
      </Circle>
      <Box
        alignContent="center"
        display="flex"
        justifyContent="space-between"
        marginBottom="10px"
        padding="10px"
        width="100%"
      >
        <Text>
          <strong>{actorName}</strong>
          {` recorded an event in `}
          <strong>{domainKey}</strong>
        </Text>
        <Text fontSize="sm" color="gray">
          {commonDate.epochToLocaleDateString(createdAt, undefined, {
            ...DateTime.TIME_SIMPLE,
            timeZoneName: 'short',
          })}
        </Text>
      </Box>
    </Box>
  );
};

const Event = ({ event }: { event: EventForRenderFragmentGQLType }) => {
  const { domainKey, actionKey, created_at: createdAt } = event;
  const domainConfig = eventConfig[domainKey];
  if (!domainConfig) {
    return <DefaultEvent event={event} />;
  }
  const actionConfig = domainConfig[actionKey];
  if (!actionConfig) {
    return <DefaultEvent event={event} />;
  }
  const ActionComponent = actionConfig.Component;
  const PanelComponent = actionConfig.PanelComponent || null;

  return (
    <AccordionItem border="none" isDisabled={!PanelComponent}>
      <AccordionButton
        _disabled={{
          opacity: 1,
          _hover: { backgroundColor: 'transparent', cursor: 'default' },
        }}
        _expanded={{
          backgroundColor: 'millet.50',
          borderWidth: '1px 1px 0',
          borderColor: 'gray.100',
        }}
        borderTop="none"
        borderBottom="1px"
        borderColor="gray.100"
      >
        <Circle size="40px">{actionConfig.icon}</Circle>
        <Box
          alignContent="center"
          display="flex"
          justifyContent="space-between"
          padding="10px"
          width="100%"
          textAlign="start"
          gap="0.5rem"
        >
          <ActionComponent event={event} />
          <Text fontSize="sm" color="gray" flexShrink={0}>
            {commonDate.epochToLocaleDateString(createdAt, undefined, {
              ...DateTime.TIME_SIMPLE,
              timeZoneName: 'short',
            })}
          </Text>
        </Box>
      </AccordionButton>
      <AccordionPanel
        borderWidth="0 1px 1px"
        borderColor="gray.100"
        pt="10px"
        pl="65px"
      >
        {PanelComponent && <PanelComponent event={event} />}
      </AccordionPanel>
    </AccordionItem>
  );
};

type EventsProps = {
  eventsMap: Record<Util.FullDateStr, EventForRenderFragmentGQLType[]>;
};
const Events = ({ eventsMap }: EventsProps) => (
  <>
    {Object.entries(eventsMap).map(([dateStr, value]) => (
      <Box key={dateStr} mt="25px" mb="25px">
        <Heading as="h3" fontSize="2xl" mb="25px">
          {commonDate.fullDateStrToHumanDateString(dateStr as Util.FullDateStr)}
        </Heading>
        <Accordion allowMultiple>
          {value.map((eventForRender) => (
            <Event key={eventForRender.id} event={eventForRender} />
          ))}
        </Accordion>
      </Box>
    ))}
  </>
);

export const EventsTable = () => {
  const { user } = useUserAuthOrThrow();
  const { pageLimit, setPageLimit, options } = usePaginationLimit();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [
    getEvents,
    { loading: eventsLoading, data: eventsData },
  ] = useGetEventsLazyQueryGQLType({});

  useEffect(() => {
    getEvents({
      variables: {
        limit: pageLimit,
        organizationId: user.organization.id,
        startDate: commonDate.optionalDateToFullDateStr(startDate),
        endDate: commonDate.optionalDateToFullDateStr(endDate),
      },
    });
  }, [startDate, endDate, pageLimit, getEvents, user.organization.id]);

  if (eventsLoading || !eventsData) {
    return <Skeleton data-testid="loading-deliverables-view" height="600px" />;
  }

  const {
    events: {
      nodes: events,
      pageInfo: { hasNextPage, hasPreviousPage, startCursor, endCursor },
    },
  } = eventsData;
  let eventsByDay: {
    [key in Util.FullDateStr]: EventForRenderFragmentGQLType[];
  } = {};
  if (events) {
    eventsByDay = _.groupBy(events, (event) => {
      const { created_at: createdAt } = event;
      return commonDate.epochMsToFullDateStr(createdAt);
    });
  }

  return (
    <Box height="100%">
      <Heading as="h2" mb="1rem">
        Event Log
      </Heading>
      <PaginationControl
        filters={
          <FilterDatePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        }
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Events per page"
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPrevious={async () => {
          getEvents({
            variables: {
              limit: pageLimit,
              organizationId: user.organization.id,
              before: startCursor,
              startDate: commonDate.optionalDateToFullDateStr(startDate),
              endDate: commonDate.optionalDateToFullDateStr(endDate),
            },
          });
        }}
        onNext={async () => {
          getEvents({
            variables: {
              limit: pageLimit,
              organizationId: user.organization.id,
              after: endCursor,
              startDate: commonDate.optionalDateToFullDateStr(startDate),
              endDate: commonDate.optionalDateToFullDateStr(endDate),
            },
          });
        }}
      >
        <>
          {_.isEmpty(events) || _.isEmpty(eventsByDay) ? (
            <Text>No events have been recorded</Text>
          ) : (
            <Events eventsMap={eventsByDay} />
          )}
        </>
      </PaginationControl>
    </Box>
  );
};
